import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { FilterSidebar } from '../components/property/FilterSidebar';
import { InteractiveMap } from '../components/property/InteractiveMap';
import { PropertyCard } from '../components/property/PropertyCard';
import { Pagination } from '../components/common/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import { REGIONS } from '../data/mockData';
import { Loader2 } from 'lucide-react';

export function DemandsPage() {
  const { demands, filters, clearFilters, isAuthenticated, user, propertiesLoading, propertiesLoaded, loadPropertiesFromApi } = useAppStore();
  const { language, t } = useI18n();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [showPhoneFor, setShowPhoneFor] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('newest');
  const [mapVisibleProperties, setMapVisibleProperties] = useState<typeof demands | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Load properties from API if not loaded
  useEffect(() => {
    if (!propertiesLoaded && !propertiesLoading) {
      loadPropertiesFromApi();
    }
  }, [propertiesLoaded, propertiesLoading, loadPropertiesFromApi]);

  // Calculate focusLocation based on selected locations
  const focusLocation = useMemo(() => {
    // If we have locations array, use the first one
    if (filters.locations && filters.locations.length > 0) {
      const firstLoc = filters.locations[0];
      if (firstLoc.lat && firstLoc.lng) {
        return { lat: firstLoc.lat, lng: firstLoc.lng, zoom: firstLoc.zoom || 11 };
      }
    }
    // Fallback to legacy locationLat/locationLng
    if (filters.locationLat && filters.locationLng) {
      return { lat: filters.locationLat, lng: filters.locationLng, zoom: 13 };
    }
    if (filters.region) {
      const region = REGIONS.find(r => r.name === filters.region);
      if (region) {
        if (filters.city) {
          const city = region.cities.find(c => c.name === filters.city);
          if (city?.lat && city?.lng) {
            return { lat: city.lat, lng: city.lng, zoom: 13 };
          }
        }
        if (region.lat && region.lng) {
          return { lat: region.lat, lng: region.lng, zoom: region.zoom || 10 };
        }
      }
    }
    return undefined;
  }, [filters.locations, filters.region, filters.city, filters.locationLat, filters.locationLng]);

  // Apply filters
  const filteredDemands = demands.filter(d => {
    if (filters.adType && d.adType !== filters.adType) return false;
    if (filters.category && d.category !== filters.category) return false;
    if (filters.subcategory && d.subcategory !== filters.subcategory) return false;
    
    // Handle multiple locations filter
    if (filters.locations && filters.locations.length > 0) {
      const matchesLocation = filters.locations.some(loc => {
        if (loc.type === 'region') {
          return d.location.region === loc.name;
        } else if (loc.type === 'subregion') {
          return d.location.city === loc.name || d.location.region === loc.region;
        } else if (loc.type === 'listing-address') {
          // For listing-address, filter to the specific demand ID
          if (loc.propertyId) {
            return d.id === loc.propertyId;
          }
          return loc.subregion ? d.location.city === loc.subregion : false;
        } else if (loc.type === 'address') {
          // For OSM address: filter by subregion
          if (loc.subregion) {
            return d.location.city === loc.subregion || 
                   d.location.city.toLowerCase().includes(loc.subregion.toLowerCase()) ||
                   loc.subregion.toLowerCase().includes(d.location.city.toLowerCase());
          }
          const itemAddress = `${d.location.address || ''} ${d.location.city} ${d.location.region}`.toLowerCase();
          return itemAddress.includes(loc.name.split(',')[0].toLowerCase());
        }
        return false;
      });
      if (!matchesLocation) return false;
    } else {
      // Fallback to single region/city filter
      if (filters.region && d.location.region !== filters.region) return false;
      if (filters.city && d.location.city !== filters.city) return false;
    }
    
    if (filters.priceMin && d.price < filters.priceMin) return false;
    if (filters.priceMax && d.price > filters.priceMax) return false;
    
    // Only with images filter
    if (filters.onlyWithImages) {
      const validImages = d.images?.filter(img => img && img.trim() !== '') || [];
      if (validImages.length === 0) return false;
    }
    
    // Urgent only filter
    if (filters.urgentOnly && !d.isUrgent) return false;
    
    return true;
  });
  
  // Sort: urgent listings first, then by selected sort
  const urgentSortedDemands = [...filteredDemands].sort((a, b) => {
    if (a.isUrgent && !b.isUrgent) return -1;
    if (!a.isUrgent && b.isUrgent) return 1;
    return 0;
  });
  
  // Filter by map bounds if map view is active and we have visible properties
  const displayedDemands = (viewMode === 'map' && mapVisibleProperties) 
    ? mapVisibleProperties 
    : urgentSortedDemands;
    
  // Sort demands
  const sortedDemands = [...displayedDemands].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'sqm-asc':
        return a.squareMeters - b.squareMeters;
      case 'sqm-desc':
        return b.squareMeters - a.squareMeters;
      case 'views':
        return b.views - a.views;
      default:
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
  });

  const sortOptions = [
    { value: 'newest', label: language === 'sr' ? 'Najnovije' : 'Newest' },
    { value: 'price-asc', label: language === 'sr' ? 'Cena: rastuće' : 'Price: Low to High' },
    { value: 'price-desc', label: language === 'sr' ? 'Cena: opadajuće' : 'Price: High to Low' },
    { value: 'sqm-asc', label: language === 'sr' ? 'Kvadratura: rastuće' : 'Area: Low to High' },
    { value: 'sqm-desc', label: language === 'sr' ? 'Kvadratura: opadajuće' : 'Area: High to Low' },
    { value: 'views', label: language === 'sr' ? 'Najpopularnije' : 'Most Popular' },
  ];

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  // Pagination
  const totalItems = sortedDemands.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedDemands = viewMode === 'map' ? sortedDemands : sortedDemands.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleShowPhone = (demandId: string) => {
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    setShowPhoneFor(demandId);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {language === 'sr' ? 'Potražnja nekretnina' : 'Wanted Properties'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {language === 'sr' 
              ? 'Pregledajte šta traže potencijalni kupci i zakupci' 
              : 'See what potential buyers and renters are looking for'}
          </p>
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 mb-8 text-white"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold mb-1">
                {language === 'sr' 
                  ? 'Imate nekretninu koja odgovara potražnji?' 
                  : 'Have a property that matches the demand?'}
              </h2>
              <p className="text-amber-100">
                {language === 'sr' 
                  ? 'Kontaktirajte oglašivača direktno i ponudite svoju nekretninu' 
                  : 'Contact the advertiser directly and offer your property'}
              </p>
            </div>
            <Link
              to="/novi-oglas"
              className="flex-shrink-0 bg-white text-amber-600 font-semibold px-6 py-3 rounded-lg hover:bg-amber-50 transition-colors"
            >
              {language === 'sr' ? 'Dodaj ponudu' : 'Add listing'}
            </Link>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <FilterSidebar 
              showAdType={true} 
              showPricePerUnit={true}
              listings={demands}
              pageType="demands"
            />
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="lg:hidden mb-4">
              <FilterSidebar 
                showAdType={true} 
                showPricePerUnit={true} 
                onClose={() => setShowFilters(false)}
                listings={demands}
                pageType="demands"
              />
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {t.filters.title}
                </button>
                <span className="text-slate-600 dark:text-slate-400">
                  {sortedDemands.length} {language === 'sr' 
                    ? (sortedDemands.length === 1 ? 'potražnja' : 'potražnji')
                    : (sortedDemands.length === 1 ? 'demand' : 'demands')}
                </span>
              </div>

              <div className="flex items-center gap-4">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-amber-600 hover:text-amber-500 text-sm"
                  >
                    {t.filters.clearFilters}
                  </button>
                )}
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mreža' : 'Grid'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Lista' : 'List'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'map' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mapa' : 'Map'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Map View */}
            {viewMode === 'map' && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden mb-6">
                <InteractiveMap
                  properties={filteredDemands}
                  height="500px"
                  showFilters={false}
                  focusLocation={focusLocation}
                  selectedRegion={filters.region}
                />
              </div>
            )}

            {/* Loading State */}
            {propertiesLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                <span className="ml-3 text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
                </span>
              </div>
            )}

            {/* Demands Grid */}
            {!propertiesLoading && sortedDemands.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center"
              >
                <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {language === 'sr' ? 'Nema potražnji za ove filtere' : 'No demands match these filters'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  {language === 'sr' 
                    ? 'Pokušajte da promenite filtere ili objavite svoju potražnju' 
                    : 'Try changing filters or post your own demand'}
                </p>
                <Link
                  to="/novi-oglas"
                  className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-amber-400 transition-colors"
                >
                  {language === 'sr' ? 'Objavi potražnju' : 'Post demand'}
                </Link>
              </motion.div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paginatedDemands.map((demand) => (
                  <PropertyCard key={demand.id} property={demand} />
                ))}
              </div>
            ) : viewMode === 'list' ? (
              <div className="flex flex-col gap-4">
                {paginatedDemands.map((demand) => (
                  <PropertyCard key={demand.id} property={demand} variant="horizontal" />
                ))}
              </div>
            ) : null}

            {/* Pagination */}
            {viewMode !== 'map' && sortedDemands.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
